import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Droplet, Gauge, Search, MapPin, Wrench } from "lucide-react";

import { EmptyState } from "../../components/EmptyState";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
  type BadgeTone,
} from "../../components/ui";
import { MESES } from "../cierres/estado";
import {
  listarConsumosAnomalos,
  type ConsumoAnomalo,
  type PuntoConsumo,
} from "./api";
import { CrearOtRevisionModal } from "./CrearOtRevisionModal";

type Tipo = "fuga" | "cero";

function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function periodoLabel(a: ConsumoAnomalo): string {
  if (!a.periodo_anio) return "—";
  return `${MESES[a.periodo_mes - 1] ?? a.periodo_mes} ${a.periodo_anio}`;
}

/** Deep-link a las lecturas del socio en el período del hallazgo. La
 *  búsqueda de /lecturas lee `q` de la URL (seed del cuadro de búsqueda). */
function lecturasHref(a: ConsumoAnomalo): string {
  const params = new URLSearchParams({
    anio: String(a.periodo_anio),
    mes: String(a.periodo_mes),
    q: a.socio_rut ?? a.socio_nombre,
  });
  return `/lecturas?${params.toString()}`;
}

/** Mini-histograma de los últimos meses. En fugas resalta la última barra
 *  (el "salto") en ámbar; en ceros resalta las barras en 0 (rose) para que
 *  el patrón salte a la vista. El resto en slate. */
function Histograma({
  historial,
  tipo,
}: {
  historial: PuntoConsumo[];
  tipo: Tipo;
}): JSX.Element | null {
  if (historial.length < 2) return null;
  const max = Math.max(...historial.map((p) => p.consumo_m3), 1);
  return (
    <div className="flex h-8 items-end gap-0.5" title="Consumo de los últimos meses (m³)">
      {historial.map((p, i) => {
        const ultimo = i === historial.length - 1;
        let cls = "bg-neutral-300";
        if (tipo === "cero" && p.consumo_m3 === 0) cls = "bg-rose-400";
        else if (tipo === "fuga" && ultimo) cls = "bg-amber-500";
        const h = Math.max(2, Math.round((p.consumo_m3 / max) * 28));
        return (
          <div
            key={`${p.anio}-${p.mes}`}
            className={`w-1.5 rounded-sm ${cls}`}
            style={{ height: `${h}px` }}
            title={`${MESES[p.mes - 1] ?? p.mes}: ${p.consumo_m3} m³`}
          />
        );
      })}
    </div>
  );
}

function factorTone(factor: number | null): BadgeTone {
  if (factor == null) return "slate";
  if (factor >= 5) return "rose";
  return "amber";
}

export function ConsumosPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const tipo: Tipo = searchParams.get("tipo") === "cero" ? "cero" : "fuga";
  const [q, setQ] = useState("");
  const [otPara, setOtPara] = useState<ConsumoAnomalo | null>(null);

  const setTipo = (t: Tipo) => {
    const next = new URLSearchParams(searchParams);
    next.set("tipo", t);
    setSearchParams(next, { replace: true });
  };

  const consumosQ = useQuery({
    queryKey: ["consumos-anomalos"],
    queryFn: listarConsumosAnomalos,
  });

  const lista = tipo === "fuga" ? consumosQ.data?.fugas ?? [] : consumosQ.data?.ceros ?? [];
  const nFugas = consumosQ.data?.fugas.length ?? 0;
  const nCeros = consumosQ.data?.ceros.length ?? 0;

  const filtrados = useMemo(() => {
    const t = norm(q.trim());
    if (!t) return lista;
    return lista.filter((a) =>
      norm(
        [a.socio_nombre, a.socio_rut, a.sector_nombre, a.arranque_codigo, a.arranque_direccion]
          .filter(Boolean)
          .join(" "),
      ).includes(t),
    );
  }, [lista, q]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Revisión de consumos"
        icon={Droplet}
        subtitle={
          <span className="block max-w-3xl">
            Contratos marcados por su patrón de consumo a lo largo de los meses.
            No son cargos ni sanciones: son avisos para{" "}
            <strong>confirmar en terreno</strong> antes de comunicar al socio. Una
            fuga probable puede ser una cañería rota; un consumo en cero puede ser
            un medidor detenido, una vivienda deshabitada o una lectura no tomada.
          </span>
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-neutral-200">
        <TabButton
          activo={tipo === "fuga"}
          onClick={() => setTipo("fuga")}
          icon={Droplet}
          label="Posibles fugas"
          count={nFugas}
        />
        <TabButton
          activo={tipo === "cero"}
          onClick={() => setTipo("cero")}
          icon={Gauge}
          label="Consumos en cero"
          count={nCeros}
        />
      </div>

      {tipo === "cero" && lista.length > 0 && (
        <p className="max-w-3xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Según el Manual SISS (Término Medio), un medidor detenido o sin lectura{" "}
          <strong>debe facturarse con el promedio, no en 0</strong>, y cambiarse
          dentro de 60 días. Revisa cada caso: aplica término medio o confirma si
          la vivienda está deshabitada.
        </p>
      )}

      {/* Buscador */}
      {lista.length > 0 && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Buscar en la revisión"
            placeholder="Buscar socio, RUT, sector o arranque…"
            className="w-72 rounded-md border border-neutral-450 py-1.5 pl-8 pr-3 text-sm shadow-sm"
          />
        </div>
      )}

      {consumosQ.isPending && <Cargando />}
      {consumosQ.isError && (
        <ErrorCarga
          error={consumosQ.error}
          mensaje="Error al cargar la revisión."
          onReintentar={() => void consumosQ.refetch()}
        />
      )}

      {consumosQ.data && lista.length === 0 && (
        <EmptyState
          icon={tipo === "fuga" ? Droplet : Gauge}
          tone="emerald"
          title={
            tipo === "fuga"
              ? "Sin fugas probables"
              : "Sin consumos en cero"
          }
          description={
            tipo === "fuga"
              ? "Ningún contrato triplicó su promedio de consumo reciente. Todo dentro de rango."
              : "Ningún contrato activo tiene lecturas en 0 m³ recientes. Todos midiendo consumo."
          }
        />
      )}

      {lista.length > 0 && filtrados.length === 0 && (
        <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-6 text-center text-sm text-neutral-500">
          Ningún registro coincide con la búsqueda.
        </p>
      )}

      {filtrados.length > 0 && (
        <TableWrap label={tipo === "fuga" ? "Posibles fugas" : "Consumos en cero"}>
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Sector / Arranque</th>
                <th scope="col" className="px-3 py-2 text-left">Período</th>
                {tipo === "fuga" ? (
                  <>
                    <th scope="col" className="px-3 py-2 text-right">Consumo mes</th>
                    <th scope="col" className="px-3 py-2 text-right">Promedio previo</th>
                    <th scope="col" className="px-3 py-2 text-center">Salto</th>
                    <th scope="col" className="px-3 py-2 text-left">Últimos meses</th>
                  </>
                ) : (
                  <>
                    <th scope="col" className="px-3 py-2 text-left">Patrón</th>
                    <th scope="col" className="px-3 py-2 text-center">Meses en 0</th>
                    <th scope="col" className="px-3 py-2 text-left">Últimos meses</th>
                  </>
                )}
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtrados.map((a) => (
                <tr key={a.contrato_id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2">
                    <Link
                      to={`/socios/${a.socio_id}/cuenta-corriente`}
                      className="font-medium text-neutral-900 hover:text-brand-700"
                    >
                      {a.socio_nombre}
                    </Link>
                    {a.socio_rut && (
                      <div className="text-xs text-neutral-500">{a.socio_rut}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-neutral-500" strokeWidth={1.5} />
                      {a.sector_nombre ?? "—"}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {a.arranque_codigo ?? ""}
                      {a.arranque_direccion ? ` · ${a.arranque_direccion}` : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">{periodoLabel(a)}</td>
                  {tipo === "fuga" ? (
                    <>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-neutral-900">
                        {a.consumo_actual} m³
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-neutral-500">
                        {a.promedio_previo != null ? `${a.promedio_previo} m³` : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge tone={factorTone(a.factor)}>
                          {a.factor != null ? `×${a.factor}` : "—"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Histograma historial={a.historial} tipo="fuga" />
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2">
                        <Badge
                          tone={
                            a.patron_cero === "detenido" ? "rose" : "amber"
                          }
                        >
                          {a.patron_cero === "detenido"
                            ? "Medidor detenido"
                            : "Intermitente"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center tabular-nums text-neutral-700">
                        {a.meses_en_cero ?? 0}
                      </td>
                      <td className="px-3 py-2">
                        <Histograma historial={a.historial} tipo="cero" />
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="link"
                        size="sm"
                        icon={Wrench}
                        onClick={() => setOtPara(a)}
                        title="Derivar a una orden de trabajo"
                      >
                        Crear OT
                      </Button>
                      <Link
                        to={lecturasHref(a)}
                        className="text-xs font-medium text-brand-700 hover:text-brand-800"
                      >
                        Ver lecturas →
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {otPara && (
        <CrearOtRevisionModal anomalia={otPara} onClose={() => setOtPara(null)} />
      )}
    </div>
  );
}

function TabButton({
  activo,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  activo: boolean;
  onClick: () => void;
  icon: typeof Droplet;
  label: string;
  count: number;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={
        "flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors " +
        (activo
          ? "border-brand-600 text-brand-700"
          : "border-transparent text-neutral-500 hover:text-neutral-700")
      }
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
      {label}
      <span
        className={
          "rounded-full px-1.5 text-xs " +
          (activo ? "bg-brand-100 text-brand-800" : "bg-neutral-100 text-neutral-500")
        }
      >
        {count}
      </span>
    </button>
  );
}
